import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Zap, Shield, Sparkles } from "lucide-react";

const POINTS = [
  {
    icon: Zap,
    title: "Tailored, not templated",
    text: "Every engagement starts with the problem — not a pre-built deck. We custom-build for your context.",
  },
  {
    icon: Shield,
    title: "Engineering rigor",
    text: "Modern stacks, observability, and security baked in. We ship systems that hold up at scale.",
  },
  {
    icon: Sparkles,
    title: "Craft you can feel",
    text: "Even our internal dashboards look beautiful. Design isn't a layer we add — it's how we think.",
  },
  {
    icon: CheckCircle2,
    title: "Outcomes, not output",
    text: "We tie our work to measurable business impact: revenue, retention, runway, reputation.",
  },
];

export default function WhyChoose() {
  return (
    <section
      id="why-choose"
      data-testid="why-choose-section"
      className="relative py-28 md:py-36 border-t border-white/5"
    >
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(37,99,235,0.10) 0%, rgba(5,5,5,0) 70%)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-400" data-testid="why-overline">
            [ Why Dataviqu ]
          </div>
          <h2
            className="mt-5 font-display text-4xl md:text-5xl font-medium tracking-tight"
            data-testid="why-heading"
          >
            Smart Solutions
            <br />
            <span className="text-zinc-500">That Actually Work.</span>
          </h2>
          <p className="mt-5 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
            From intelligent AI systems and data-driven insights to financial strategy and impactful design — we create tailored solutions that solve real business challenges, not generic templates.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: POINTS.indexOf(p) * 0.07 }}
                className="glass card-hover rounded-2xl p-7 md:p-9"
                data-testid={`why-point-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl border border-blue-500/30 bg-blue-500/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-medium tracking-tight">{p.title}</h3>
                    <p className="mt-2 text-zinc-400 text-sm md:text-base leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
